import {
    Injectable,
    Injector,
    TemplateRef,
    Type,
    InjectionToken,
    ViewContainerRef,
    ComponentRef,
    EmbeddedViewRef
} from '@angular/core';
import { DrawerComponent } from './drawer.component';
import { ComponentPortal, TemplatePortal } from '@angular/cdk/portal';
import { OverlayConfig, Overlay, ScrollStrategy, OverlayRef } from '@angular/cdk/overlay';
import {  Directionality } from '@angular/cdk/bidi';
import { Subject } from 'rxjs';

const ESCAPE_KEY = 'Escape';
const ANIMATION_DURATION = 260;

export type DrawerContentType<T> = TemplateRef<T> | Type<T>;
export const DRAWER_DATA = new InjectionToken<object>('DrawerData');

export class DrawerConfig<D = object> {
    viewContainerRef?: ViewContainerRef;
    data?: D;
    scrollStrategy?: ScrollStrategy;
    panelClass?: string | string[];
    hasBackdrop?: boolean;
    direction?: 'ltr' | 'rtl' | Directionality;
    minWidth?: string | number;
    minHeight?: string | number;
    maxWidth?: string | number;
    maxHeight?: string | number;
    backdropClass?: string | string[];
    closeOnNavigation?: boolean;
}

@Injectable()
export class DrawerService<T> {

    public close$ = new Subject<void>();
    private drawerContainer: DrawerComponent;
    private componentRef: ComponentRef<T> | EmbeddedViewRef<T>;
    private overlayRef?: OverlayRef;

    constructor(
        private readonly overlay: Overlay,
        private readonly injector: Injector,
    ) { }

    open<D extends object>(content: DrawerContentType<T>, userConfig: DrawerConfig<D>): void {
        const config = this.applyConfigDefaults(userConfig, new DrawerConfig());

        // Overlay recycling.
        // If a drawer is summoned on top of an existing one, the overlay will be kept but the content updated.
        if (!this.overlayRef) {
            this.overlayRef = this.createOverlay(config);

            this.drawerContainer = this.attachDrawerContainer(config);
            this.overlayRef.backdropClick().subscribe(() => this.close());
            this.overlayRef.keydownEvents().subscribe((keydown: KeyboardEvent) => {
                if (keydown.key === ESCAPE_KEY) {
                    this.close();
                }
            });
        }

        this.destroyDrawerContent();

        setTimeout(() => this.drawerContainer.isOpened = true, 0);

        this.componentRef = this.attachDrawerContent<D>(content, this.drawerContainer, config);
    }

    close(): void {
        this.drawerContainer.isOpened = false;
        this.close$.next();

        setTimeout(() => {
            this.destroyDrawerContent();
            this.overlayRef.detachBackdrop();
            this.overlayRef.dispose();
            this.overlayRef = undefined;
        }, ANIMATION_DURATION);
    }

    private destroyDrawerContent(): void {
        if (this.componentRef) {
            this.componentRef.destroy();
        }
    }

    private createOverlay(config: DrawerConfig): OverlayRef {
        const overlayConfig = this.getOverlayConfig(config);
        return this.overlay.create(overlayConfig);
    }

    private getOverlayConfig(drawerConfig: DrawerConfig): OverlayConfig {
        const overlayConfig = new OverlayConfig({
            positionStrategy: this.overlay.position().global(),
            scrollStrategy: drawerConfig.scrollStrategy || this.overlay.scrollStrategies.block(),
            panelClass: drawerConfig.panelClass,
            hasBackdrop: drawerConfig.hasBackdrop,
            direction: drawerConfig.direction,
            minWidth: drawerConfig.minWidth,
            minHeight: drawerConfig.minHeight,
            maxWidth: drawerConfig.maxWidth,
            maxHeight: drawerConfig.maxHeight,
            // @TODO : upgrade material version
            // disposeOnNavigation: drawerConfig.closeOnNavigation
        });

        if (drawerConfig.backdropClass) {
            overlayConfig.backdropClass = drawerConfig.backdropClass;
        }

        return overlayConfig;
    }

    private attachDrawerContainer(config: DrawerConfig): DrawerComponent {

        const userInjector = config && config.viewContainerRef && config.viewContainerRef.injector;
        const injector = Injector.create({
            providers: [
                { provide: DrawerConfig, useValue: config }
            ],
            parent: userInjector,
        });
        const drawerPortal = new ComponentPortal(DrawerComponent, config.viewContainerRef, injector);
        const drawerRef = this.overlayRef.attach<DrawerComponent>(drawerPortal);

        return drawerRef.instance;

    }

    private applyConfigDefaults<D>(config?: DrawerConfig<D>, defaultOptions?: DrawerConfig<D>): DrawerConfig<D> {
        return { ...defaultOptions, ...config };
    }

    private attachDrawerContent<D extends object>(
        content: DrawerContentType<T>,
        drawerContainer: DrawerComponent,
        config: DrawerConfig<D>
    ): ComponentRef<T> | EmbeddedViewRef<T> {

        if (content instanceof TemplateRef) {
            return drawerContainer.attachTemplatePortal(
                // <any> context cannot be assign to T type
                new TemplatePortal<T>(content, null, <any>{ data: config.data })
            );
        }

        const injector = this.createInjector(config, drawerContainer);

        return drawerContainer.attachComponentPortal<T>(
            new ComponentPortal(content, undefined, injector));
    }

    private createInjector<D>(
        config: DrawerConfig<D>,
        drawerContainer: DrawerComponent): Injector {

        const userInjector = config && config.viewContainerRef && config.viewContainerRef.injector;

        return Injector.create({
            providers: [
                { provide: DrawerComponent, useValue: drawerContainer },
                { provide: DRAWER_DATA, useValue: config.data }
            ],
            parent: userInjector || this.injector,
        });
    }

}
